import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useParams } from "react-router";

import type { AnswerItem } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useSlug } from "@/hooks/useSlug";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { surveyMutations } from "@/queries/surveys/mutations";
import { useSurveyQueries } from "@/queries/surveys/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { SurveyInfoSection } from "../details/components/SurveyInfoSection";
import { SurveyRespondQuestionCard } from "./components/SurveyRespondQuestionCard";
import { SurveyRespondQuestionGroupCard } from "./components/SurveyRespondQuestionGroupCard";
import { SurveyRespondSuccessModal } from "./components/SurveyRespondSuccessModal";

export type SurveyRespondFormValues = {
  [questionId: string]: Omit<AnswerItem, "questionId">;
};

export function SurveyRespondPage(): React.ReactNode {
  const clockDate = useCurrentDate();
  const [isSuccessModalOpened, successModalHandler] = useBool(false);

  const { id: surveyId } = useParams<{ id: string }>();
  const { state: locationState } = useLocation();
  const { t } = useTranslation();
  const slug = useSlug();
  const postHog = usePostHog();

  const surveyQueries = useSurveyQueries();
  const {
    data: surveyDetails,
    error: errorSurveyDetails,
    isPending: isPendingSurveyDetails,
  } = useQuery({
    ...surveyQueries.details({ surveyId: surveyId!, category: locationState?.category || "feelAtHome" }),
    enabled: !!surveyId,
  });
  const addSurveyAnswersMutation = surveyMutations.useAddSurveyAnswers();
  const addViewCountMutation = communityFeedMutations.useAddViewCount();

  // Mutate view count on mount if it has not been tracked or mutated in this session yet
  useEffect(() => {
    if (!surveyDetails || surveyDetails.viewTracked || addViewCountMutation.submittedAt) return;

    addViewCountMutation.mutate({
      data: {
        viewedSurveys: [surveyDetails.id],
      },
    });
  }, [addViewCountMutation, surveyDetails]);

  const allQuestions = surveyDetails?.questionGroups.flatMap((group) => group.questions) || [];
  const defaultValues = allQuestions.reduce<SurveyRespondFormValues>((acc, question) => {
    if (question.type === "rating") {
      acc[question.id] = {
        rating: 3,
      };
    } else if (question.type === "ranking") {
      acc[question.id] = {
        ranking: question.answers?.map((answer) => ({
          answerId: answer.id,
          sort: question.answers?.indexOf(answer) || 0,
        })),
      };
    } else {
      acc[question.id] = {};
    }

    return acc;
  }, {});
  const form = useForm<SurveyRespondFormValues>({
    values: defaultValues,
  });

  if (isPendingSurveyDetails) {
    return <FullSizeLoader withPadding />;
  }

  const error = errorSurveyDetails;
  if (error) {
    return <ErrorPage error={error} />;
  }

  if (surveyDetails.endsAt && new Date(surveyDetails.endsAt) < clockDate) {
    return <ErrorPage error={404} />;
  }

  const handleSubmit = async (formValues: SurveyRespondFormValues) => {
    await addSurveyAnswersMutation.mutateAsync({
      surveyId: surveyId!,
      data: {
        answers: Object.entries(formValues).map(([questionId, answer]) => ({
          questionId,
          ...answer,
        })),
      },
    });

    postHog.capture("submitted_survey_respond_form", {
      survey_id: surveyId,
      source: "web",
    });
    successModalHandler.setTrue();
  };

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={surveyDetails.title.trim() || t("page.survey-respond.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.message-feed.title"),
                to: routes.communityFeed.list({ slug }),
              },
              {
                name: t("page.survey-respond.title"),
              },
            ]}
          />
        }
      >
        <PageGrid.Row>
          <PageGrid.Column size="50%">
            <Form.Root formMethods={form} onSubmit={handleSubmit}>
              <Stack gap="md">
                <SurveyInfoSection survey={surveyDetails} showCountdown />
                {surveyDetails.scope === "project" &&
                  surveyDetails.questionGroups[0].questions.map((question) => (
                    <SurveyRespondQuestionCard key={question.id} {...{ question }} />
                  ))}
                {surveyDetails.scope === "automated" &&
                  surveyDetails.questionGroups.map((questionGroup) => (
                    <SurveyRespondQuestionGroupCard key={questionGroup.order} {...{ questionGroup }} />
                  ))}

                <Card as="footer">
                  <Inline justify="end" gap="sm">
                    <Button
                      type="submit"
                      disabled={!!addSurveyAnswersMutation.submittedAt}
                      isLoading={addSurveyAnswersMutation.isPending}
                    >
                      {t("page.survey-respond.form.button.submit")}
                    </Button>
                  </Inline>
                </Card>
              </Stack>
            </Form.Root>
          </PageGrid.Column>
        </PageGrid.Row>
      </DocumentPage>

      <SurveyRespondSuccessModal
        message={surveyDetails.completedMessage}
        isOpened={isSuccessModalOpened}
        onOpenChange={successModalHandler.set}
      />
    </>
  );
}
